import { useId } from 'react'
import Box from '@mui/material/Box'

import type { LocaleCode } from '../../i18n/resolveLocale'

/**
 * Flags for the language switcher, drawn as inline SVG.
 *
 * Not emoji. Regional-indicator flag emoji (🇫🇮 and friends) are simply not drawn on Windows:
 * Segoe UI Emoji ships no flag glyphs, so the browser falls back to rendering the two
 * regional-indicator letters and the switcher reads "GB", "FI", "RU". Roughly half the
 * visitors to a Finnish holiday-rental site are on Windows, so the emoji approach fails for
 * the people most likely to need the language switcher in the first place.
 *
 * Inline SVG rather than an icon library or a CDN sprite: seven flags of simple geometry cost
 * about a hundred bytes each, render identically everywhere, need no network request, and
 * work offline in the installed PWA.
 *
 * All seven share a 3:2 viewBox so they line up in a menu regardless of the real aspect ratio
 * of the flag. That is a deliberate inaccuracy; a ragged column of differently shaped
 * rectangles reads worse than a tidy one.
 */

const ASPECT = { width: 60, height: 40 }

export function FlagIcon({
  locale,
  size = 22,
  title,
}: {
  locale: LocaleCode
  /** Rendered width in px; height follows the 3:2 ratio. */
  size?: number
  title?: string
}) {
  // The Union Jack needs clip paths, and two switchers on one page would collide on a
  // hard-coded id. useId gives each instance its own.
  const uid = useId().replace(/:/g, '')

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${ASPECT.width} ${ASPECT.height}`}
      width={size}
      height={size * (ASPECT.height / ASPECT.width)}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      sx={{
        display: 'block',
        flexShrink: 0,
        borderRadius: '3px',
        // A hairline keeps the white-heavy flags (Finland, Poland) from bleeding into a
        // white menu background.
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.16)',
      }}
    >
      {renderFlag(locale, uid)}
    </Box>
  )
}

function renderFlag(locale: LocaleCode, uid: string) {
  switch (locale) {
    // ---------------------------------------------------------------- United Kingdom
    case 'en':
      return (
        <>
          <clipPath id={`${uid}-frame`}>
            <rect width="60" height="40" />
          </clipPath>
          <clipPath id={`${uid}-diag`}>
            {/* Restricts the red diagonals to alternating quadrants, which is what gives the
                Union Jack its offset pinwheel rather than plain crossed stripes. */}
            <path d="M30,20 h30 v20 z v20 h-30 z h-30 v-20 z v-20 h30 z" />
          </clipPath>
          <g clipPath={`url(#${uid}-frame)`}>
            <rect width="60" height="40" fill="#012169" />
            <path d="M0,0 L60,40 M60,0 L0,40" stroke="#FFFFFF" strokeWidth="8" />
            <path
              d="M0,0 L60,40 M60,0 L0,40"
              clipPath={`url(#${uid}-diag)`}
              stroke="#C8102E"
              strokeWidth="5"
            />
            <path d="M30,0 v40 M0,20 h60" stroke="#FFFFFF" strokeWidth="13" />
            <path d="M30,0 v40 M0,20 h60" stroke="#C8102E" strokeWidth="8" />
          </g>
        </>
      )

    // ---------------------------------------------------------------- Finland
    case 'fi':
      return (
        <>
          <rect width="60" height="40" fill="#FFFFFF" />
          {/* Nordic cross: the vertical bar sits left of centre. */}
          <rect x="17" width="10" height="40" fill="#003580" />
          <rect y="15" width="60" height="10" fill="#003580" />
        </>
      )

    // ---------------------------------------------------------------- Russia
    case 'ru':
      return (
        <>
          <rect width="60" height="40" fill="#FFFFFF" />
          <rect y="13.34" width="60" height="13.33" fill="#0039A6" />
          <rect y="26.67" width="60" height="13.33" fill="#D52B1E" />
        </>
      )

    // ---------------------------------------------------------------- Ukraine
    case 'uk':
      return (
        <>
          <rect width="60" height="20" fill="#0057B7" />
          <rect y="20" width="60" height="20" fill="#FFD700" />
        </>
      )

    // ---------------------------------------------------------------- Israel
    case 'he':
      return (
        <>
          <rect width="60" height="40" fill="#FFFFFF" />
          <rect y="5" width="60" height="4.5" fill="#0038B8" />
          <rect y="30.5" width="60" height="4.5" fill="#0038B8" />
          {/* Star of David as two overlaid open triangles. */}
          <path
            d="M30,13 L36.5,24.5 L23.5,24.5 Z"
            fill="none"
            stroke="#0038B8"
            strokeWidth="1.7"
          />
          <path
            d="M30,27 L23.5,15.5 L36.5,15.5 Z"
            fill="none"
            stroke="#0038B8"
            strokeWidth="1.7"
          />
        </>
      )

    // ---------------------------------------------------------------- Germany
    case 'de':
      return (
        <>
          <rect width="60" height="13.34" fill="#000000" />
          <rect y="13.34" width="60" height="13.33" fill="#DD0000" />
          <rect y="26.67" width="60" height="13.33" fill="#FFCE00" />
        </>
      )

    // ---------------------------------------------------------------- Poland
    case 'pl':
      return (
        <>
          <rect width="60" height="20" fill="#FFFFFF" />
          <rect y="20" width="60" height="20" fill="#DC143C" />
        </>
      )

    // ---------------------------------------------------------------- Estonia
    // Blue, black, white in equal horizontal bands. The official blue is Pantone 285C.
    case 'et':
      return (
        <>
          <rect width="60" height="13.34" fill="#0072CE" />
          <rect y="13.34" width="60" height="13.33" fill="#000000" />
          <rect y="26.67" width="60" height="13.33" fill="#FFFFFF" />
        </>
      )

    default:
      return <rect width="60" height="40" fill="#CFD8DC" />
  }
}
